import { useState, useCallback } from 'react';
import permissionService from '../services/permissionService';

const usePermissions = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [permissionTree, setPermissionTree] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 100,
    total_pages: 0
  });
  const [selectedPermission, setSelectedPermission] = useState(null);

  // Cache for permission trees by category
  const [permissionTreeCache, setPermissionTreeCache] = useState({
    system: null,
    project: null
  });
  const [loadedCategories, setLoadedCategories] = useState(new Set());

  // Get all permissions with filtering and pagination
  const getAllPermissions = useCallback(async (category, search = null, page = 1, limit = 100) => {
    try {
      setLoading(true);
      setError(null);
      const data = await permissionService.getAllPermissions(category, search, page, limit);
      setPermissions(data.permissions);
      setPagination({
        total: data.total,
        page: data.page,
        limit: data.limit,
        total_pages: data.total_pages
      });
      return data;
    } catch (err) {
      setError(err.message || err || 'Failed to fetch permissions');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get permissions as hierarchical tree with caching
  const getPermissionTree = useCallback(async (category = null, forceRefresh = false) => {
    try {
      // Check if category is already loaded and we have cached data
      if (!forceRefresh && category && loadedCategories.has(category) && permissionTreeCache[category]) {
        console.log(`Using cached permission tree for category: ${category}`);
        setPermissionTree(permissionTreeCache[category]);
        return permissionTreeCache[category];
      }

      console.log(`Fetching permission tree from API for category: ${category}`);
      setLoading(true);
      setError(null);
      const data = await permissionService.getPermissionTree(category);

      // Update the tree state
      setPermissionTree(data.tree);

      // Update cache if category is specified
      if (category) {
        setPermissionTreeCache(prev => ({
          ...prev,
          [category]: data.tree
        }));
        setLoadedCategories(prev => new Set([...prev, category]));
      }

      return data.tree;
    } catch (err) {
      setError(err.message || err || 'Failed to fetch permission tree');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadedCategories, permissionTreeCache]);

  // Get single permission by ID
  const getPermissionById = useCallback(async (permissionId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await permissionService.getPermissionById(permissionId);
      setSelectedPermission(data);
      return data;
    } catch (err) {
      setError(err.message || err || 'Failed to fetch permission');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new permission
  const createPermission = useCallback(async (permissionData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await permissionService.createPermission(permissionData);
      // Add to permissions list if it exists
      if (response.data) {
        setPermissions(prev => [...prev, response.data]);
      }
      return response;
    } catch (err) {
      setError(err.message || err || 'Failed to create permission');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update an existing permission
  const updatePermission = useCallback(async (permissionId, permissionData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await permissionService.updatePermission(permissionId, permissionData);
      // Update in permissions list
      if (response.data) {
        setPermissions(prev =>
          prev.map(perm =>
            perm.id === permissionId ? response.data : perm
          )
        );
        // Update selected permission if it matches
        if (selectedPermission && selectedPermission.id === permissionId) {
          setSelectedPermission(response.data);
        }
      }
      return response;
    } catch (err) {
      setError(err.message || err || 'Failed to update permission');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedPermission]);

  // Delete a permission
  const deletePermission = useCallback(async (permissionId, force = false) => {
    try {
      setLoading(true);
      setError(null);
      const response = await permissionService.deletePermission(permissionId, force);
      // Remove from permissions list
      setPermissions(prev =>
        prev.filter(perm => perm.id !== permissionId)
      );
      // Clear selected permission if it matches
      if (selectedPermission && selectedPermission.id === permissionId) {
        setSelectedPermission(null);
      }
      return response;
    } catch (err) {
      setError(err.message || err || 'Failed to delete permission');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedPermission]);

  // Move permission up or down
  const movePermission = useCallback(async (permissionId, direction) => {
    try {
      setLoading(true);
      setError(null);
      const response = await permissionService.movePermission(permissionId, direction);
      // Update display_order in permissions list
      if (response.updated_permissions) {
        setPermissions(prev =>
          prev.map(perm => {
            const updated = response.updated_permissions.find(u => u.id === perm.id);
            return updated ? { ...perm, display_order: updated.display_order } : perm;
          })
        );
      }
      return response;
    } catch (err) {
      setError(err.message || err || 'Failed to move permission');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get permission usage
  const getPermissionUsage = useCallback(async (permissionId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await permissionService.getPermissionUsage(permissionId);
      return data;
    } catch (err) {
      setError(err.message || err || 'Failed to get permission usage');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Validate permission path
  const validatePath = useCallback(async (path, excludeId = null) => {
    try {
      // Don't set loading for validation (non-blocking)
      setError(null);
      const data = await permissionService.validatePath(path, excludeId);
      return data;
    } catch (err) {
      setError(err.message || err || 'Failed to validate path');
      throw err;
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear selected permission
  const clearSelectedPermission = useCallback(() => {
    setSelectedPermission(null);
  }, []);

  // Clear permission tree cache (use after create/update/delete operations)
  const clearPermissionTreeCache = useCallback((category = null) => {
    if (category) {
      console.log(`Clearing cache for category: ${category}`);
      setPermissionTreeCache(prev => ({
        ...prev,
        [category]: null
      }));
      setLoadedCategories(prev => {
        const newSet = new Set(prev);
        newSet.delete(category);
        return newSet;
      });
    } else {
      console.log('Clearing all permission tree cache');
      setPermissionTreeCache({
        system: null,
        project: null
      });
      setLoadedCategories(new Set());
    }
  }, []);

  return {
    loading,
    error,
    permissions,
    permissionTree,
    pagination,
    selectedPermission,
    getAllPermissions,
    getPermissionTree,
    getPermissionById,
    createPermission,
    updatePermission,
    deletePermission,
    movePermission,
    getPermissionUsage,
    validatePath,
    clearError,
    clearSelectedPermission,
    setSelectedPermission,
    clearPermissionTreeCache
  };
};

export default usePermissions;
